import { Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { DecimalInput } from "@/components/common/DecimalInput";
import { Chip } from "@/components/common/StatusBadge";
import { ProductPicker } from "@/components/orders/ProductPicker";
import { fmtMoney, fmtNumber, parseDecimalInput } from "@/lib/format";
import { CATALOG_CATEGORY_SHORT, TRANSPORT_MODE_LABELS, unitShort } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const MASS_UNITS = [
  { value: "t", label: "tonn" },
  { value: "m3", label: "m³" },
  { value: "lass", label: "lass" },
];

const Group = ({ title, hint, children, actions }) => (
  <fieldset className="space-y-3">
    <div className="flex items-start justify-between gap-3">
      <div>
        <legend className="text-[13px] font-semibold leading-5">{title}</legend>
        {hint ? <p className="text-xs leading-5 text-muted-foreground">{hint}</p> : null}
      </div>
      {actions}
    </div>
    {children}
  </fieldset>
);

const num = (v) => {
  const n = parseDecimalInput(v);
  return Number.isNaN(n) ? 0 : n;
};

/**
 * Kalkyle-inndata (administrasjon).
 * inputs.items = [{ key, catalog_item_id, quantity, unit, unit_price, item (katalogobjekt) }]
 * Avstand/køyretid/bil kjem frå ruta (logistics).
 */
export const KalkylePanel = ({ inputs, onChange, catalog = [], vehicle = null, logistics = null, settings = null, estimate = null, customer = null, testId = "kalkyle" }) => {
  const set = (patch) => onChange({ ...inputs, ...patch });
  const items = inputs.items || [];
  const agreements = Object.fromEntries((customer?.price_agreements || []).map((a) => [a.catalog_item_id, a]));
  const catalogById = Object.fromEntries(catalog.map((c) => [c.id, c]));

  const addItem = (cat) => {
    const existing = items.find((it) => it.catalog_item_id === cat.id);
    if (existing) {
      set({ items: items.map((it) => (it === existing ? { ...it, quantity: String(num(it.quantity) + 1).replace(".", ",") } : it)) });
      return;
    }
    set({ items: [...items, { key: `${cat.id}-${Date.now()}`, catalog_item_id: cat.id, quantity: "", unit: cat.unit, unit_price: "" }] });
  };
  const setItem = (key, patch) => set({ items: items.map((it) => (it.key === key ? { ...it, ...patch } : it)) });
  const removeItem = (key) => set({ items: items.filter((it) => it.key !== key) });

  const defaultPrice = (cat) => {
    const a = agreements[cat?.id];
    if (a && a.unit_price !== null && a.unit_price !== undefined) return { price: Number(a.unit_price), source: "kundeavtale" };
    if (cat && cat.unit_price !== null && cat.unit_price !== undefined) return { price: Number(cat.unit_price), source: "katalog" };
    return { price: null, source: null };
  };

  const extras = inputs.extras || [];
  const setExtra = (i, patch) => set({ extras: extras.map((e, idx) => (idx === i ? { ...e, ...patch } : e)) });
  const vehicleRate = vehicle?.catalog_item_id ? catalogById[vehicle.catalog_item_id] : null;
  const ferryItem = settings?.ferry_item_name ? catalog.find((c) => c.name === settings.ferry_item_name) : null;
  const ratePlaceholder = inputs.transport_mode === "per_km" ? (settings ? `Standard ${fmtNumber(settings.transport_rate_per_km, 2)} kr/km` : "kr/km") : inputs.transport_mode === "per_lass" ? "kr per lass" : vehicleRate?.unit_price !== undefined && vehicleRate?.unit_price !== null ? `Katalog ${fmtNumber(vehicleRate.unit_price, 2)} kr/t` : "kr per time";
  const hasMass = items.some((it) => catalogById[it.catalog_item_id]?.category === "masse" || it.unit === "t");

  return (
    <div className="space-y-6" data-testid={testId}>
      <Group title="Produkt og tenester" hint="Søk i produktlista og legg til så mange linjer du vil. Kundeavtale vinn over katalogpris; du kan overstyre pris per linje.">
        <ProductPicker catalog={catalog} agreements={agreements} onPick={addItem} testId={`${testId}-picker`} />
        {items.length ? (
          <ul className="divide-y divide-border rounded-lg border border-border bg-background" data-testid={`${testId}-items`}>
            {items.map((it) => {
              const cat = catalogById[it.catalog_item_id];
              const def = defaultPrice(cat);
              const price = it.unit_price !== "" && it.unit_price !== null && it.unit_price !== undefined ? num(it.unit_price) : def.price;
              const qty = num(it.quantity);
              const amount = price !== null && qty > 0 ? qty * price : null;
              const isMass = cat?.category === "masse";
              return (
                <li key={it.key} className="p-3" data-testid={`${testId}-item`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium" data-testid={`${testId}-item-name`}>{cat?.name || it.item_name || "Ukjend vare"}</p>
                      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        {cat?.sku ? <span className="num">{cat.sku}</span> : null}
                        <span>{CATALOG_CATEGORY_SHORT[cat?.category] || ""}</span>
                        {def.source === "kundeavtale" && (it.unit_price === "" || it.unit_price === null) ? <Chip tone="info" outline>Kundeavtale</Chip> : null}
                        {it.unit_price !== "" && it.unit_price !== null && it.unit_price !== undefined ? <Chip tone="warning" outline>Pris overstyrt</Chip> : null}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className={cn("num text-sm font-medium", amount === null && "text-muted-foreground")} data-testid={`${testId}-item-amount`}>{amount === null ? "–" : fmtMoney(amount)}</span>
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => removeItem(it.key)} aria-label="Fjern linje" data-testid={`${testId}-item-remove`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-[1fr_minmax(88px,0.8fr)_1fr] gap-2 sm:grid-cols-[140px_120px_160px]">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Mengd</Label>
                      <DecimalInput value={it.quantity ?? ""} onChange={(v) => setItem(it.key, { quantity: v })} placeholder="0" className="h-10 bg-card" data-testid={`${testId}-item-qty`} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Eining</Label>
                      {isMass ? (
                        <Select value={it.unit || cat?.unit || "t"} onValueChange={(v) => setItem(it.key, { unit: v })}>
                          <SelectTrigger className="h-10 bg-card" data-testid={`${testId}-item-unit`}><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-popover">{MASS_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <div className="flex h-10 items-center rounded-md border border-border bg-muted px-3 text-sm text-muted-foreground" data-testid={`${testId}-item-unit`}>{unitShort(it.unit || cat?.unit)}</div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Pris per {unitShort(it.unit || cat?.unit)}</Label>
                      <DecimalInput value={it.unit_price ?? ""} onChange={(v) => setItem(it.key, { unit_price: v })} placeholder={def.price === null ? "Pris manglar" : fmtNumber(def.price, 2)} className="h-10 bg-card" data-testid={`${testId}-item-price`} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="rounded-lg border border-dashed border-border bg-background px-3 py-4 text-center text-sm text-muted-foreground" data-testid={`${testId}-items-empty`}>Ingen linjer enno. Søk etter t.d. «pukk», «gravemaskin» eller eit varenummer.</p>
        )}
      </Group>

      <Group title="Transport" hint={vehicle ? `Bil: ${vehicle.name}${vehicle.payload_kg ? ` · nyttelast ${fmtNumber(vehicle.payload_kg / 1000, 1)} t` : ""}${logistics?.distance_km ? ` · ${fmtNumber(logistics.distance_km, 1)} km / ${fmtNumber(logistics.duration_min, 0)} min éin veg` : " · rute er ikkje rekna ut"}` : "Vel bil under «Stader og rute» for å rekne lass og transport."}>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Prisingsmåte</Label>
            <Select value={inputs.transport_mode || "per_time"} onValueChange={(v) => set({ transport_mode: v })}>
              <SelectTrigger data-testid={`${testId}-transport-mode`}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover">{Object.entries(TRANSPORT_MODE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {inputs.transport_mode !== "inkludert" ? (
            <div className="space-y-1.5">
              <Label htmlFor={`${testId}-rate`}>Sats (tomt = standard)</Label>
              <DecimalInput id={`${testId}-rate`} value={inputs.transport_rate ?? ""} onChange={(v) => set({ transport_rate: v })} placeholder={ratePlaceholder} data-testid={`${testId}-rate`} />
            </div>
          ) : null}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-loads`}>Tal lass {estimate?.loads && !inputs.loads_override ? <span className="font-normal text-muted-foreground">(auto: {estimate.loads})</span> : null}</Label>
            <Input id={`${testId}-loads`} type="number" inputMode="numeric" min="1" step="1" value={inputs.loads_override ?? ""} onChange={(e) => set({ loads_override: e.target.value ? Number(e.target.value) : null })} placeholder={hasMass ? "Auto" : "1"} className="tabular" data-testid={`${testId}-loads`} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-loading`}>Lasting (min/lass)</Label>
            <DecimalInput id={`${testId}-loading`} value={inputs.loading_min ?? ""} onChange={(v) => set({ loading_min: v })} placeholder={settings ? String(settings.loading_minutes) : "15"} data-testid={`${testId}-loading`} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-unloading`}>Lossing (min/lass)</Label>
            <DecimalInput id={`${testId}-unloading`} value={inputs.unloading_min ?? ""} onChange={(v) => set({ unloading_min: v })} placeholder={settings ? String(settings.unloading_minutes) : "10"} data-testid={`${testId}-unloading`} />
          </div>
        </div>
        <div className="space-y-1.5 sm:max-w-[240px]">
          <Label htmlFor={`${testId}-empty`}>Tomkøyring til hentestad (km)</Label>
          <DecimalInput id={`${testId}-empty`} value={inputs.empty_km ?? ""} onChange={(v) => set({ empty_km: v })} placeholder="Intern kostnad, ikkje fakturert" data-testid={`${testId}-empty-km`} />
        </div>
      </Group>

      <Group title="Ferje, bom og tillegg" hint="Vidarefakturert utan påslag. Takstar er overslag til faktisk billett ligg føre.">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-ferry-count`}>Ferjebillettar (stk)</Label>
            <Input id={`${testId}-ferry-count`} type="number" inputMode="numeric" min="0" step="1" value={inputs.ferry_count ?? ""} onChange={(e) => set({ ferry_count: e.target.value ? Number(e.target.value) : null })} placeholder="0" className="tabular" data-testid={`${testId}-ferry-count`} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-ferry-price`}>Pris per billett</Label>
            <DecimalInput id={`${testId}-ferry-price`} value={inputs.ferry_unit_price ?? ""} onChange={(v) => set({ ferry_unit_price: v })} placeholder={ferryItem?.unit_price ? `Katalog ${fmtNumber(ferryItem.unit_price, 2)}` : "kr"} data-testid={`${testId}-ferry-price`} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${testId}-toll`}>Bompengar (kr)</Label>
            <DecimalInput id={`${testId}-toll`} value={inputs.toll_amount ?? ""} onChange={(v) => set({ toll_amount: v })} placeholder="0" data-testid={`${testId}-toll`} />
          </div>
        </div>
        <div className="space-y-2">
          {extras.map((e, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`${testId}-extra-${i}`}>
              <Input value={e.description || ""} onChange={(ev) => setExtra(i, { description: ev.target.value })} placeholder="Tillegg, t.d. ventetid" className="flex-1" data-testid={`${testId}-extra-desc-${i}`} />
              <DecimalInput value={e.amount ?? ""} onChange={(v) => setExtra(i, { amount: v })} placeholder="kr" className="w-28" data-testid={`${testId}-extra-amount-${i}`} />
              <Button type="button" variant="ghost" size="icon" className="h-10 w-10 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => set({ extras: extras.filter((_, idx) => idx !== i) })} aria-label="Fjern tillegg" data-testid={`${testId}-extra-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-muted-foreground hover:text-foreground" onClick={() => set({ extras: [...extras, { description: "", amount: "" }] })} data-testid={`${testId}-add-extra`}>+ Legg til tillegg</Button>
        </div>
      </Group>

      <Group title="Rabatt">
        <div className="space-y-1.5 sm:max-w-[240px]">
          <Label htmlFor={`${testId}-discount`}>Rabatt (%)</Label>
          <DecimalInput id={`${testId}-discount`} value={inputs.discount_pct ?? ""} onChange={(v) => set({ discount_pct: v })} placeholder={customer?.discount_pct ? `Kundeavtale ${fmtNumber(customer.discount_pct, 1)} %` : "0"} data-testid={`${testId}-discount`} />
        </div>
      </Group>
    </div>
  );
};
